/**
 * useMediaDB — manages public.media table in Supabase.
 * Includes Supabase Storage upload via useStorageUpload.
 * No mock fallback. Real errors surfaced.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useStorageUpload } from "@/hooks/useStorageUpload";
import type { MediaItem } from "@/mocks/admin";

function rowToMedia(row: Record<string, unknown>): MediaItem {
  return {
    id: row.id as string,
    name: row.name as string,
    url: row.url as string,
    type: (row.type as MediaItem["type"]) || "image",
    size: (row.size as string) || "",
    uploadedAt: (row.created_at as string) || new Date().toISOString(),
    dimensions: row.dimensions as string | undefined,
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function useMediaDB() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { uploadFile, progress: uploadProgress } = useStorageUpload();

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from("media")
      .select("*")
      .order("created_at", { ascending: false });

    if (dbError) {
      setError(dbError.message);
      setMedia([]);
    } else {
      setMedia((data ?? []).map((r) => rowToMedia(r as Record<string, unknown>)));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  /**
   * Upload a File to Supabase Storage, then save a row in public.media.
   * Returns { data: MediaItem, error: string | null }.
   */
  const uploadMedia = useCallback(
    async (file: File): Promise<{ data: MediaItem | null; error: string | null }> => {
      // 1. Upload to Storage
      const { url, error: uploadError } = await uploadFile(file);
      if (uploadError || !url) {
        return { data: null, error: uploadError ?? "Upload fehlgeschlagen." };
      }

      // 2. Get image dimensions (best-effort, only for images)
      let dimensions: string | undefined;
      if (file.type.startsWith("image/")) {
        try {
          dimensions = await new Promise<string>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(`${img.naturalWidth}x${img.naturalHeight}`);
            img.onerror = () => resolve("");
            img.src = URL.createObjectURL(file);
          });
        } catch {
          dimensions = undefined;
        }
      }

      // 3. Save row in public.media
      const { data, error: dbError } = await supabase
        .from("media")
        .insert({
          name: file.name,
          url,
          type: file.type.startsWith("video/") ? "video" : "image",
          size: formatBytes(file.size),
          dimensions,
        })
        .select()
        .maybeSingle();

      if (dbError) return { data: null, error: dbError.message };
      if (!data) return { data: null, error: "Unbekannter Fehler beim Speichern." };

      const newItem = rowToMedia(data as Record<string, unknown>);
      await fetchMedia();
      return { data: newItem, error: null };
    },
    [uploadFile, fetchMedia]
  );

  /**
   * Save a media item from a URL (no file upload).
   */
  const addMediaByUrl = useCallback(
    async (item: Omit<MediaItem, "id" | "uploadedAt">): Promise<{ data: MediaItem | null; error: string | null }> => {
      const { data, error: dbError } = await supabase
        .from("media")
        .insert({
          name: item.name,
          url: item.url,
          type: item.type,
          size: item.size,
          dimensions: item.dimensions,
        })
        .select()
        .maybeSingle();

      if (dbError) return { data: null, error: dbError.message };
      if (!data) return { data: null, error: "Unbekannter Fehler." };

      const newItem = rowToMedia(data as Record<string, unknown>);
      await fetchMedia();
      return { data: newItem, error: null };
    },
    [fetchMedia]
  );

  const deleteMedia = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      const { error: dbError } = await supabase.from("media").delete().eq("id", id);
      if (dbError) return { error: dbError.message };
      await fetchMedia();
      return { error: null };
    },
    [fetchMedia]
  );

  return {
    media,
    loading,
    error,
    uploadProgress,
    refetch: fetchMedia,
    uploadMedia,
    addMediaByUrl,
    deleteMedia,
  };
}
